// BRIEF 94 §1b — THE OVERLAP SEPARATOR, WITHOUT PAINT.
//
// ── WHAT WAS WRONG ──────────────────────────────────────────────────────────
//
// Three renderers draw a stack of overlapping initials badges, and all three
// separated the discs by painting a ring in a SURFACE token:
//
//   src/App.tsx            `coAvs`  — `border: 1px solid var(--br)` (Border)
//                                     `outline: 2px solid var(--bd)` (Page background)
//   src/components/CaseTaskRow.tsx   `outline: 2px solid var(--bd)`   (brief 93 §1a)
//   src/lib/matterListCfg.tsx        `outline: 2px solid var(--bd)`
//
// A ring in the page's colour is invisible ON THE PAGE and visible everywhere
// else, and only the case list sits on the page. MEASURED in a browser, light
// theme: `--bd` resolves to `rgb(240,242,245)` and the list's own table surface
// to `rgb(232,234,237)` — so it is visible even there. `--br` is
// `rgb(209,213,219)`, a grey hairline on every badge on every surface. Against
// them, the ONE ring that means something — `boxShadow: isMe ? --ac` — was one
// of three ring-shaped devices, and the other two were on every badge.
//
// ── WHY THE CALLER CANNOT SUPPLY ITS SURFACE COLOUR ─────────────────────────
//
// §1b offered two ways out: the caller passes the colour of the surface the
// stack sits on, or the separation is achieved without paint. The first is not
// merely laborious, it is IMPOSSIBLE on one of the surfaces, and that was
// measured rather than argued: the subtask drawer's background is
// `color-mix(in srgb, var(--cb) 14%, transparent)` — it composites to
// `color(srgb 0.227 0.263 0.345 / 0.14)` over whatever card is behind it. No
// solid colour matches a translucent surface over an unknown parent. A separator
// that is right on two surfaces and wrong on the third is the defect this file
// exists to remove, not a smaller version of it.
//
// ── SO THE HOLE IS CUT, NOT PAINTED ────────────────────────────────────────
//
// The badge BEHIND is masked where the badge in FRONT overlaps it, plus a small
// gap. What shows through the hole is the surface itself — whatever it is, on
// any theme, including a translucent one — so there is no colour to get wrong
// and nothing to keep in sync when a surface changes. A mask clips the element's
// whole paint, the `--ac` ring included, which is exactly right: the front badge
// covers the ring of the one behind it precisely where it covers its disc.
//
// 118 §1 — THE LAST SENTENCE WAS HALF TRUE, AND THE HALF THAT WAS NOT IS A
// DEFECT THIS FILE SHIPPED FOR FOUR BRIEFS. A mask does clip the ring where the
// hole is; it also clipped it EVERYWHERE ELSE, because `mask-clip` defaults to
// the border box and a box-shadow spread paints outside it. The masked badge is
// the viewer's, so the one ring that means something was invisible on every row
// where he is not the principal. `stackCut` below now sizes its own box past
// the ring and lifts the border-box clip; the sentence above is true as written
// for the first time.
//
// A browser without `mask-image` drops the declaration and renders the stack as
// plain overlapping discs — no ring, which is the outcome §1 is asking for, so
// the degradation is toward the goal rather than away from it.

// ── BRIEF 116 §2 — AT MOST TWO BADGES, AND THE AREA IS A FIXED WIDTH ────────
//
// RULED, in Laurent's words: a row shows AT MOST TWO badges — (1) the
// collaborator assigned to the task/event, (2) the CURRENT USER if a subtask of
// that row is assigned to him. Everyone else leaves the row for the badge area's
// `title`. Before this, both stacks grew with the row's people: MEASURED on
// watt's 2026/001, task 205 (assigned=[4] + five distinct subtask assignees)
// drew 102px of badge and STRETCHED THE WHOLE COLUMN to 106 — for every row in
// that table, which is exactly the failure 93 §1a named when it introduced the
// overlap ("a busier row widened the whole column and pushed the timer, the
// counter and the title right for every row") and did not prevent.
//
// THE PRINCIPAL IS NOT INVENTED HERE — it is read off what each kind already
// calls one:
//
//   task   `assignedCoId`, which the WIRE shape defines as `assigned[0]`
//          (server/src/lib/taskShape.js:23 — "the first explicit assignee";
//          the frontend has modelled a task as single-assignee since C65).
//   event  the attending responsible partner, which brief 95 §2c ruled the
//          event's principal BY INHERITANCE and sorts to the head of the stack
//          today; with no RP attending, `assigned[0]` — the same first-element
//          notion the task shape already blesses, applied to the same column
//          (AgendaEvent.assigned, aliased `assignedCollaborators` in
//          routes/agenda.js:177).
//
// The two slots are the stack's own geometry, not a new number: one badge is
// `BADGE_SIZE`, the second slides `BADGE_OVERLAP` over it, so the pair is 38.

/** The one badge diameter every stack in the app draws (95 §2a). */
export const BADGE_SIZE = 22;
/** The stack's negative margin, as a positive number (93 §1a, 95 §2a). */
export const BADGE_OVERLAP = 6;
/**
 * The reserved badge area: two slots at the stack's own geometry.
 * 22 + (22 - 6) = 38. Reserved as a MINIMUM rather than a cap, because the 75
 * §3 archived earmark is a mark and not a badge and may not be cut into (93
 * §1b) — a row whose principal is archived is the one state that still exceeds
 * this, and it is named in the PR rather than silently clipped.
 */
export const BADGE_AREA_W = BADGE_SIZE * 2 - BADGE_OVERLAP;

/**
 * BRIEF 118 §1 — THE VIEWER'S RING, AS A NUMBER `stackCut` CAN READ.
 *
 * The `--ac` self-ring is a box-shadow SPREAD: `0 0 0 1.5px var(--ac)`, written
 * at three sites (SharedUI.tsx's two `InitialsBadge` branches and the event
 * stack's own override, App.tsx). It is the only thing a badge paints OUTSIDE
 * its border box — which is why the mask below has to know about it, and why a
 * literal in three files could not have told it.
 */
export const BADGE_RING_W = 1.5;

/** The ring itself, so the three sites that draw it and the mask that must not
 *  clip it cannot drift apart. 94 §1a: one ring, and it is the viewer's. */
export const BADGE_SELF_RING = `0 0 0 ${BADGE_RING_W}px var(--ac)`;

/** The two fields this file reads off a subtask row, and no more: the badge
 *  rule asks who holds it and whether it is live. Written out rather than
 *  reached for with `any` — `ratchets.js` may only ever shrink. */
type SubtaskLike = { archived?: boolean; assignedCoId?: number | null };

/**
 * §2's second badge, as a predicate: the viewer holds a LIVE subtask of this
 * row and is not already its principal. "Live" is the house word and the house
 * filter — not archived (`liveSubs`, CaseTaskRow.tsx:286; `liveDocs` beside
 * it) — so a DONE subtask still earns the badge, exactly as the row-level
 * permission union has always counted one (alertEngine.js:313).
 */
export function viewerHoldsSubtask(
  subtasks: unknown,
  viewerId: number | null | undefined,
  principalId: number | null | undefined,
): boolean {
  if (viewerId == null || viewerId === principalId) return false;
  const arr: readonly SubtaskLike[] = Array.isArray(subtasks) ? subtasks : [];
  return arr.some((s) => !!s && !s.archived && s.assignedCoId === viewerId);
}

// ── BRIEF 117 §4 — THE VIEWER'S BADGE IS ABOUT INVOLVEMENT, NOT ABOUT WORK ──
//
// Laurent: the self-highlight "seems to have been lost". MEASURED before
// touching anything, in a browser, on watt's 2026/001 — and the ring code is
// INTACT everywhere. `isMe` reaches `InitialsBadge` at every slot of both
// stacks and the `--ac` box-shadow renders whenever it is passed. What 116 §2
// changed is not the styling; it is WHO GETS A BADGE AT ALL.
//
//   task rows   principal slot   ring drawn, opacity 1        — never lost
//               viewer slot      ring drawn, opacity 0.75     — faded, see below
//   event cards every card       NO RING ON ANY               — the loss
//
// The event half is the report. Case 2026/001 has no responsible partners, so
// every event's principal falls back to `assigned[0]` — collaborator 6 — while
// the viewer (1) is an attendee of SEVEN of its ten events and the principal of
// none. Under `viewerHoldsSubtask` he earns slot 2 only by holding a live
// subtask of the event, and those events have none: he vanished from his own
// rows into the area's `title`. Before 116 the stack grew with the row's people
// and sorted him last with `zIndex: 2`, so his ringed badge was always there.
//
// SO THE FIX IS THE PREDICATE, AT ITS ONE DEFINITION — not a style, and not a
// second rule per host. Slot 2 becomes "the viewer, when he is involved in this
// row and is not already its principal", and involvement is the union of the
// two ways a row names a person: being ON it (assigned / attending) and holding
// live work under it. The count is untouched — still AT MOST TWO badges, still
// `BADGE_AREA_W`, still no "+N" chip. Only the identity of badge 2 widens.
export function viewerInRow(
  /** The row's own people: `Task.assigned` / `AgendaEvent.assignedCollaborators`. */
  members: unknown,
  subtasks: unknown,
  viewerId: number | null | undefined,
  principalId: number | null | undefined,
): boolean {
  if (viewerId == null || viewerId === principalId) return false;
  const arr: readonly unknown[] = Array.isArray(members) ? members : [];
  if (arr.some((m) => Number(m) === viewerId)) return true;
  return viewerHoldsSubtask(subtasks, viewerId, principalId);
}

/**
 * The tail the badges no longer carry: every person the row's stack COULD have
 * shown before this brief, comma-joined, in the order it showed them. The
 * caller supplies the id list and the resolver, because the two hosts resolve
 * against different collections (`co` on a task row, `collaborators` in App).
 */
export function assigneeTip<P>(
  ids: readonly (number | null | undefined)[],
  resolve: (id: number) => P | null | undefined,
  name: (p: P) => string,
): string {
  const seen = new Set<number>();
  const out: string[] = [];
  for (const raw of ids) {
    if (raw == null) continue;
    const id = Number(raw);
    if (!Number.isFinite(id) || seen.has(id)) continue;
    seen.add(id);
    const p = resolve(id);
    if (p) out.push(name(p));
  }
  return out.join(", ");
}

// ── BRIEF 117 §3 — THE ROW'S PEOPLE, SECTIONED, IN ONE PLACE ────────────────
//
// §3 asks a hover panel to list everyone involved with a row, split by HOW they
// are involved, and to be built once — "the 116 `badgeStack.ts` already
// computes the row's people — extend that, do not re-derive per host". This is
// that extension: `assigneeTip`'s dedupe-and-resolve, kept, and split in two.
//
// The two hosts resolve against different collections (`co` on a task row,
// `collaborators` in App), so the resolver arrives as an argument exactly as it
// does for `assigneeTip`.
//
// ENUMERATED: a person who is BOTH on the row and holds a subtask of it appears
// in BOTH sections. The sections answer "how is this person involved", and the
// honest answer for such a person is "twice". Suppressing the second line would
// make the panel disagree with the row.
export type BadgePerson = { initials: string; name: string };

/**
 * What `coIni` and `pname` (src/utils.ts:582, :592) actually read off a person,
 * written out so neither host needs an `any` at the call site — `ratchets.js`
 * may only ever shrink, and a new `any` in new code is what it counts.
 */
export type NameableCo = {
  initials?: string | null;
  title?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  name?: string | null;
};

function _resolveList<P>(
  ids: unknown,
  resolve: (id: number) => P | null | undefined,
  toPerson: (p: P) => BadgePerson,
): BadgePerson[] {
  const seen = new Set<number>();
  const out: BadgePerson[] = [];
  for (const raw of Array.isArray(ids) ? ids : []) {
    if (raw == null) continue;
    const id = Number(raw);
    if (!Number.isFinite(id) || seen.has(id)) continue;
    seen.add(id);
    const p = resolve(id);
    if (p) out.push(toPerson(p));
  }
  return out;
}

/**
 * `main` — the row's own people, in the order the row names them
 * (`Task.assigned` / `AgendaEvent.assignedCollaborators`).
 * `subs` — the assignees of its LIVE subtasks, first occurrence wins. "Live" is
 * the house filter used everywhere else here: not archived.
 */
export function badgeSections<P>(
  members: unknown,
  subtasks: unknown,
  resolve: (id: number) => P | null | undefined,
  toPerson: (p: P) => BadgePerson,
): { main: BadgePerson[]; subs: BadgePerson[] } {
  const subIds = (Array.isArray(subtasks) ? (subtasks as SubtaskLike[]) : [])
    .filter((s) => !!s && !s.archived)
    .map((s) => s.assignedCoId);
  return {
    main: _resolveList(members, resolve, toPerson),
    subs: _resolveList(subIds, resolve, toPerson),
  };
}

/** How far the next badge in a stack slides over the previous one. */
export type StackCut = {
  /** Badge diameter in px. */
  size: number;
  /** The stack's negative margin, as a positive number. */
  overlap: number;
  /**
   * Which side the badge IN FRONT of this one sits on — i.e. where the hole
   * goes. `"right"` for a stack that paints later-in-front (the default DOM
   * order), `"left"` for one that paints earlier-in-front (brief 94 §2's task
   * stack, where the principal is raised above its tail).
   */
  side: "left" | "right";
  /**
   * Visible gap between the two discs, in px. See `DEFAULT_GAP` for why it is
   * 1.5 and what 3 looked like.
   */
  gap?: number;
};

/**
 * BRIEF 95 §2b — 3 -> 1.5, AND THE REASON IS NOT THE ONE §2b GAVE.
 *
 * The hole's radius is `size/2 + gap`, so `gap` px of the SURFACE show between
 * the two discs. 3px was chosen here to clear the 1.5px `--ac` ring, and §2b's
 * account of what went wrong was that the surfaces differ — a near-page-coloured
 * table row on the case list, something darker on a task. MEASURED IN A BROWSER,
 * light theme, by decoding the screenshot and reading the scanline through both
 * badge centres: THEY ARE THE SAME SURFACE. The case list's row and the task row
 * both composite to `rgb(232,234,237)` (`.table-wrap`), and at gap 3 both show
 * exactly three pixels of it. Nothing in the surfaces distinguishes them either.
 *
 * What does differ is the BADGE, not the ground: `CaseTaskRow`'s subtask tail
 * carries `opacity: 0.75`, so `--cb` rgb(58,67,88) composites to rgb(101,108,125)
 * and the light crescent separates a dark disc from a pale one — which is what
 * reads as an outline drawn AROUND the pale discs. Same number, same surface,
 * different neighbour.
 *
 * So the answer is still to narrow it, and the number is chosen from the
 * rendered result rather than from the theory:
 *
 *     gap 3    3 surface pixels    an outline
 *     gap 1.5  1 pixel + antialias a hairline          <- this
 *     gap 1    1 pixel             a hairline, and 0.5px into the ring below
 *     gap 0.5  NO surface pixel    the discs merge
 *
 * 1.5 is also the smallest value that still equals the ring it was protecting:
 * `size/2 + 1.5` puts the hole's edge exactly on the outer edge of a front
 * badge's 1.5px `--ac` ring (SharedUI.tsx:335, :364), so the ONE ring that means
 * something always lands on the surface and never on its neighbour's disc. §2b
 * permits clipping into it; there is no reason to take that when a hairline is
 * already reached without it.
 *
 * The third surface §2b names, an open subtask drawer, HOLDS NO STACK — measured
 * with five drawers open: `SubtaskChecklist` draws one badge per row and never
 * two that overlap, so there is no cut there to look at. Its composite, for the
 * record, is `color(srgb 0.227451 0.262745 0.345098 / 0.14)` over `.table-wrap`
 * = rgb(208,211,216).
 */
const DEFAULT_GAP = 1.5;

/**
 * The style fragment that cuts the neighbouring badge out of this one. Spread it
 * onto every badge that has a badge in front of it; the frontmost badge in a
 * stack takes nothing.
 */
/**
 * BRIEF 118 §1 — AND THE MASK WAS EATING THE ONE RING IN THE APP THAT MEANS
 * SOMETHING.
 *
 * §1: the viewer's self-ring "is STILL not on Laurent's rows". MEASURED, at
 * every badge site, over the four ways a viewer can relate to a row — and the
 * ring's CONDITION is right everywhere. `isMe` reaches `InitialsBadge` at every
 * slot, the `--ac` box-shadow is in the computed style of every badge that
 * should have one, and 117 §4's `viewerInRow` puts him in slot 2 in all three
 * involved cases. The ring is DECLARED and INVISIBLE, which is why reading the
 * computed style — 117's instrument — could not see it.
 *
 * A CSS mask clips the element's whole rendering to the mask, and `mask-clip`
 * defaults to the BORDER BOX. The ring is a box-shadow spread: it paints 1.5px
 * OUTSIDE that box. So the mask removed all of it — every pixel, not a corner —
 * and it did so on exactly one badge per stack: the one carrying `stackCut`,
 * which since 116 §2 is by construction THE VIEWER'S.
 *
 * That is the whole of Laurent's report, in one sentence: a row where he is the
 * principal draws his badge in slot 1, unmasked, ringed ("a lone ringed TP");
 * a row where he is an attendee or holds a subtask draws it in slot 2, masked,
 * ringless ("TS TP", "TP TP"). Two surfaces looked different because his
 * involvement differed, not because they render differently.
 *
 * THE FIX, at the one definition: grow the mask's own box past the ring and
 * stop it clipping to the border box. `mask-clip: no-clip` lifts the border-box
 * clip; the image is then sized `100% + 2*PAD` and offset `-PAD`, so the mask
 * covers the ring instead of ending on it, and `at` shifts by the same PAD
 * because the image's origin moved.
 *
 * PAD IS `2 * BADGE_RING_W`, AND THE HEADROOM IS MEASURED, NOT DECORATIVE.
 * Shot at 8x on watt, a row with the viewer in slot 2:
 *
 *     mask off        the full ring, and the discs merge   (proves the cause)
 *     pad 1.5 (=ring) the ring's bottom arc cut flat       — the image ENDS on
 *                     the shadow's outer edge, and the last row of pixels goes
 *                     with the boundary
 *     pad 3 (=2*ring) whole ring, hole intact              <- this
 *     pad 4           identical to pad 3
 *
 * A browser without `mask-clip: no-clip` degrades to the old picture — a cut
 * ring, not a broken stack.
 */
export function stackCut({ size, overlap, side, gap = DEFAULT_GAP }: StackCut) {
  // The neighbour's centre, relative to this badge's own box: its box starts at
  // `size - overlap`, so its centre is `1.5*size - overlap` from this left edge,
  // which is `size/2 - overlap` beyond this badge's right edge — and the same
  // distance before its left edge on the other side.
  const off = size / 2 - overlap;
  const pad = BADGE_RING_W * 2;
  // `at` is measured from the MASK IMAGE's origin, which now sits `pad` left of
  // and above the border box — so both forms move by `pad`.
  const at =
    side === "right" ? `calc(100% + ${off - pad}px)` : `${pad - off}px`;
  const r = size / 2 + gap;
  const hole = `radial-gradient(circle ${r}px at ${at} 50%, rgba(0,0,0,0) 100%, #000 100%)`;
  const box = `calc(100% + ${pad * 2}px)`;
  const origin = `${-pad}px ${-pad}px`;
  return {
    maskImage: hole,
    WebkitMaskImage: hole,
    maskRepeat: "no-repeat",
    WebkitMaskRepeat: "no-repeat",
    maskSize: `${box} ${box}`,
    WebkitMaskSize: `${box} ${box}`,
    maskPosition: origin,
    WebkitMaskPosition: origin,
    maskClip: "no-clip",
    WebkitMaskClip: "no-clip",
  } as const;
}
